// frontend/src/features/campaign/components/list/CampaignCard.js
import React from 'react';
import { getStatusBadgeClass, getStatusLabel, getChannelIcon } from '../../utils/campaignUtils';

export default function CampaignCard({ campaign, onSelectCampaign, onDeleteCampaign }) {
  const pauseRequestedBy = campaign.pauseRequestedBy || campaign.governance?.pauseRequestedBy;
  const isPauseRequested = campaign.status === 'ACTIVE' && Boolean(pauseRequestedBy);

  return (
    <div className="cmp-card" onClick={() => onSelectCampaign(campaign, isPauseRequested ? 6 : 1)}>
      {/* Card Top Header */}
      <div className="cmp-card-header">
        <div className="cmp-card-title-box">
          <h3 className="cmp-card-title">{campaign.name}</h3>
          {campaign.product && (
            <span className="cmp-product-tag">
              <i className="fas fa-cube" /> {campaign.product}
            </span>
          )}
        </div>
        <div className="cmp-card-header-actions">
          {campaign.status === 'ACTIVE' && pauseRequestedBy && (
            <span
              className="cmp-pause-requested-badge"
              title={`Pause requested by ${pauseRequestedBy}`}
            >
              <i className="fas fa-hand-paper" /> Pause Requested
            </span>
          )}
          <span className={`cmp-status-badge ${getStatusBadgeClass(campaign.status)}`}>
            <span className="cmp-status-dot" />
            {getStatusLabel(campaign.status)}
          </span>
          <button
            className="cmp-btn-icon danger"
            title="Delete Campaign"
            onClick={(e) => onDeleteCampaign(campaign.id, e)}
          >
            <i className="fas fa-trash-alt" />
          </button>
        </div>
      </div>

      {/* Objective */}
      <p className="cmp-card-objective">
        <strong>Objective:</strong> {campaign.objective}
      </p>

      {/* Target Profile Tags */}
      <div className="cmp-targeting-box">
        <div className="cmp-target-item">
          <i className="fas fa-globe-asia" />
          <span>
            <strong>Region:</strong> {campaign.targetMarket}
          </span>
        </div>
        <div className="cmp-target-item">
          <i className="fas fa-building" />
          <span>
            <strong>Sector:</strong> {campaign.industrySector || campaign.targetCompanyType}
          </span>
        </div>
        <div className="cmp-target-item">
          <i className="fas fa-user-tie" />
          <span>
            <strong>Personas:</strong> {campaign.targetPersonas.join(', ')}
          </span>
        </div>
      </div>

      {/* Channels & Duration */}
      <div className="cmp-channel-timeline-row">
        <div className="cmp-channel-group">
          <span className="cmp-meta-label">Channels:</span>
          {campaign.channels.map((ch) => (
            <span key={ch} className="cmp-channel-badge">
              <i className={getChannelIcon(ch)} /> {ch}
            </span>
          ))}
        </div>
        <div className="cmp-duration-info">
          <i className="fas fa-calendar-alt" />
          <span>
            Duration: <strong>{campaign.durationDays} Days</strong>
            {campaign.status === 'ACTIVE' && ` (Day ${campaign.currentDay})`}
          </span>
        </div>
      </div>

      {/* Forecast Highlights */}
      <div className="cmp-kpi-strip">
        <div className="cmp-kpi-cell">
          <span className="cmp-kpi-cell-label">Audience Size</span>
          <span className="cmp-kpi-cell-val">
            <i className="fas fa-users" /> {campaign.audienceCount}
          </span>
        </div>
        <div className="cmp-kpi-cell">
          <span className="cmp-kpi-cell-label">Est. Replies</span>
          <span className="cmp-kpi-cell-val">
            <i className="fas fa-reply" /> {campaign.expectedReplies}
          </span>
        </div>
        <div className="cmp-kpi-cell">
          <span className="cmp-kpi-cell-label">Est. Meetings</span>
          <span className="cmp-kpi-cell-val">
            <i className="fas fa-calendar-check" /> {campaign.expectedMeetings}
          </span>
        </div>
        <div className="cmp-kpi-cell highlight">
          <span className="cmp-kpi-cell-label">Est. Pipeline</span>
          <span className="cmp-kpi-cell-val">{campaign.expectedPipeline}</span>
        </div>
      </div>

      {/* Audit Info */}
      <div className="cmp-card-audit-row">
        <span>
          <i className="fas fa-user-edit" /> By: {campaign.createdBy || 'Sales Member'}
        </span>
        {campaign.approvedBy ? (
          <span className="cmp-approved-by-tag">
            <i className="fas fa-check-circle" /> Approved by {campaign.approvedBy}
          </span>
        ) : campaign.status === 'PENDING_APPROVAL' ? (
          <span className="cmp-pending-by-tag">
            <i className="fas fa-hourglass-half" /> Pending Manager Review
          </span>
        ) : campaign.status === 'CHANGES_REQUESTED' ? (
          <span className="cmp-changes-requested-by-tag">
            <i className="fas fa-undo-alt" /> Revisions Requested
          </span>
        ) : null}
      </div>

      {/* Card Footer */}
      <div className="cmp-card-footer">
        <span className="cmp-created-date">Created on {campaign.createdAt}</span>
        <div className="cmp-card-actions">
          <button
            className="cmp-btn-secondary-sm"
            onClick={(e) => {
              e.stopPropagation();
              onSelectCampaign(campaign, isPauseRequested ? 6 : 1);
            }}
          >
            <i className="fas fa-sliders-h" /> Inspect Strategy
          </button>
          <button
            className={
              isPauseRequested ? 'cmp-btn-primary-sm cmp-btn-review-pause' : 'cmp-btn-primary-sm'
            }
            onClick={(e) => {
              e.stopPropagation();
              onSelectCampaign(campaign, isPauseRequested ? 6 : 1);
            }}
          >
            {isPauseRequested ? (
              <>
                <i className="fas fa-hand-paper" /> Review Pause Request{' '}
                <i className="fas fa-arrow-right" />
              </>
            ) : (
              <>
                View Details <i className="fas fa-arrow-right" />
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
